// Pause handling for the transcript.
//
// Chrome's Web Speech API finalises a result whenever the speaker pauses, so each
// FINAL entry is effectively one pause-bounded utterance. The boundary between two
// finals is therefore a reliable "a pause happened here" signal. The *duration* is
// only approximate — we know when each segment's result arrived, not the exact
// silence length — so anything we surface from the gap is labelled approximate.

export const PAUSE_GAP_MS = 1500

export type ConfirmedSegment = { text: string; ts: number }

// Gap (ms) between a segment and the previous one. 0 for the first segment.
export const gapBefore = (segs: ConfirmedSegment[], i: number): number =>
  i <= 0 ? 0 : Math.max(0, segs[i].ts - segs[i - 1].ts)

export const isPause = (gapMs: number): boolean => gapMs >= PAUSE_GAP_MS

// Readable single-string rendering with explicit pause markers, used for the
// stored committed transcript so offline readers see the same structure.
export const formatWithPauses = (segs: ConfirmedSegment[]): string =>
  segs
    .map((s, i) => {
      if (i === 0) return s.text
      const gap = gapBefore(segs, i)
      return isPause(gap) ? ` ⟨pause ~${(gap / 1000).toFixed(1)}s⟩ ${s.text}` : ` ${s.text}`
    })
    .join('')
    .trim()
